import { HttpClient } from '@angular/common/http';
import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, map, of, timer } from 'rxjs';
import { URLS } from '@shared/constants/api-request.urls';
import { TASI_STOCK_ID } from '@shared/constants/app.constants';
import { POLLING_INTERVALS } from '@shared/constants/polling.constants';
import {
  MARKET_OPEN_TRANSITION_WINDOW_UTC,
  MARKET_CLOSE_TRANSITION_WINDOW_UTC,
  UtcTimeWindow,
} from '@shared/constants/market.constants';

// Windows never cross midnight, so no wraparound handling is needed.
export function isWithinUtcWindow(date: Date, window: UtcTimeWindow): boolean {
  const minutes = date.getUTCHours() * 60 + date.getUTCMinutes();
  const start = window.startHour * 60 + window.startMinute;
  const end = window.endHour * 60 + window.endMinute;
  return minutes >= start && minutes <= end;
}

@Injectable({
  providedIn: 'root',
})
export class MarketService {
  http = inject(HttpClient);

  getStatistics() {
    return this.http.get<any>(`${URLS.MARKET}/${TASI_STOCK_ID}`).pipe(map((res) => res.data));
  }

  private _isOpenToday = signal<boolean | null>(null);
  readonly isOpenToday = this._isOpenToday.asReadonly();

  private lastFetchedAt: number | null = null;

  /** Scoped to the caller's lifetime — stops polling once destroyRef's owner is destroyed. */
  startMarketStatePolling(destroyRef: DestroyRef): void {
    timer(0, POLLING_INTERVALS.MARKET_STATE_TICK)
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe(() => {
        if (this.shouldFetchNow(new Date())) this.fetchMarketState();
      });
  }

  private shouldFetchNow(now: Date): boolean {
    if (this.lastFetchedAt === null) return true; // always fetch once, on first mount

    // Open window: keep polling until we see the market actually open.
    if (isWithinUtcWindow(now, MARKET_OPEN_TRANSITION_WINDOW_UTC)) {
      return this._isOpenToday() !== true;
    }
    // Close window: keep polling until we see the market actually closed.
    if (isWithinUtcWindow(now, MARKET_CLOSE_TRANSITION_WINDOW_UTC)) {
      return this._isOpenToday() !== false;
    }
    // Outside both windows the state won't change, so there's nothing to poll for.
    return false;
  }

  private fetchMarketState(): void {
    this.http
      .get<{ data: { isOpenToday: boolean | null } }>(`${URLS.HOME}/market-state`)
      .pipe(
        map((res) => res.data.isOpenToday),
        catchError(() => of(null)) // sentinel: keep last known value on error
      )
      .subscribe((value) => {
        this.lastFetchedAt = Date.now();
        if (value !== null) this._isOpenToday.set(value);
      });
  }
}
